import PropTypes from "prop-types";
import { useEffect } from "react";
import { PrimaryFormOTPField } from "react-common-ui-library";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { USER_TYPE_CONSUMER } from "../../Constants/AccountSetupConstants";
import {
  ENABLE_ACCOUNT,
  FORGOT_PASSWORD,
  FORGOT_PASSWORD_ROUTE,
  LOGIN_ROUTE,
  LOG_IN,
  RESET_PASSWORD_ROUTE,
} from "../../Constants/CommonConstants";
import {
  SLICE_AUTHORIZATION,
  THUNK_POST_REFRESH_JWT_TOKEN,
} from "../../Constants/SliceAndThunkConstants";
import {
  OTP_INVALID_ERROR,
  VALID_OTP,
  VERIFY_ACCOUNT_FORM_NAME,
  isValidOTP,
} from "../../Constants/VerifyAccountConstants";
import {
  clearAuthorizationApiResponse,
  generateToken,
} from "../../Features/AuthorizationSlice";
import {
  getAccountDetails,
  putSelfEnable,
  resetLoginValidateOTPDescription,
} from "../../Features/LoginSlice";
import { manageApiResponseSelector } from "../../Selectors/ApiResponseSelector";
import { manageLoginSelector } from "../../Selectors/LoginSelector";
import { manageVerificationSelector } from "../../Selectors/VerificationSelector";

/**
 * @description Displays the Verify Account OTP Input Field
 * @param {string} previousScreen specifies the previous screen for verify account
 * @returns {ReactNode} Renders the OTP Input field to the user
 */
export const VerifyAccountOTPField = ({ previousScreen }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { userName, fpactUserRole, isPasswordExpired, isPasswordChangeFlag } =
    useSelector((state) => manageLoginSelector(state));

  const { validateOTPDescription } = useSelector((state) =>
    manageVerificationSelector(state, previousScreen)
  );

  const { apiResponses } = useSelector((state) =>
    manageApiResponseSelector(
      state,
      SLICE_AUTHORIZATION,
      THUNK_POST_REFRESH_JWT_TOKEN
    )
  );

  const { isSuccess } = apiResponses;

  useEffect(() => {
    if (validateOTPDescription === VALID_OTP) {
      switch (previousScreen) {
        case LOG_IN:
          isPasswordExpired || isPasswordChangeFlag
            ? navigate(`/${LOGIN_ROUTE}/${RESET_PASSWORD_ROUTE}`, {
                state: {
                  previousScreen: LOGIN_ROUTE,
                  isConsumer: fpactUserRole === USER_TYPE_CONSUMER,
                },
              })
            : dispatch(generateToken());
          break;
        case ENABLE_ACCOUNT:
          dispatch(putSelfEnable());
          break;
        case FORGOT_PASSWORD:
          navigate(`/${FORGOT_PASSWORD_ROUTE}/${RESET_PASSWORD_ROUTE}`, {
            state: {
              previousScreen: FORGOT_PASSWORD,
              isConsumer: fpactUserRole === USER_TYPE_CONSUMER,
            },
          });
          dispatch(resetLoginValidateOTPDescription());
          break;
        default:
          break;
      }
    }
  }, [
    validateOTPDescription,
    navigate,
    previousScreen,
    dispatch,
    userName,
    fpactUserRole,
    isPasswordExpired,
    isPasswordChangeFlag,
  ]);

  useEffect(() => {
    if (isSuccess) {
      dispatch(getAccountDetails());
      dispatch(clearAuthorizationApiResponse(THUNK_POST_REFRESH_JWT_TOKEN));
    }
  }, [dispatch, isSuccess]);

  return (
    <PrimaryFormOTPField
      formProps={{
        name: VERIFY_ACCOUNT_FORM_NAME,
        rules: {
          required: OTP_INVALID_ERROR,
          validate: {
            isValidOTP: (v) => isValidOTP(v),
          },
        },
      }}
      labelProps={{ isRequired: true }}
    />
  );
};

VerifyAccountOTPField.propTypes = {
  /**
   * specifies the previous screen
   */
  previousScreen: PropTypes.string.isRequired,
};